import React, { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  Button,
  Card,
  Container,
  Group,
  Stack,
  Text,
  Title,
  Loader,
  Center,
  ActionIcon,
  Badge,
  Grid,
  Tooltip,
  TextInput,
  Modal,
  Textarea,
  Table,
  ThemeIcon,
  Collapse,
  Select,
  MultiSelect,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconTrash,
  IconEdit,
  IconDeviceFloppy,
  IconPlus,
  IconArrowsExchange,
  IconPlayerPlay,
  IconCheck,
  IconAlertTriangle,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconSparkles,
  IconRowInsertBottom,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { hasMappingRole, mappingRoleColor, mappingRoleLabel } from "./mappingRole";
import { useTranslation } from "react-i18next";
import {
  getMappingProfileById,
  deleteMappingProfile,
  updateMappingProfile,
  previewMappingProfile,
  suggestMappings,
  getImports,
  getImport,
} from "../api";
import { getPimSchemaById, getPimAttributeDefinitions } from "../Pim/pimApi";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import RuleEditor from "./RuleEditor";

export default function MappingProfileDetail({ params }) {
  const { t } = useTranslation("imports");
  const [, navigate] = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [ruleEditorOpened, setRuleEditorOpened] = useState(false);

  // Schema and attribute lookup state
  const [sourceSchema, setSourceSchema] = useState(null);
  const [targetSchema, setTargetSchema] = useState(null);
  const [targetAttributes, setTargetAttributes] = useState([]);
  const [sourceAttributes, setSourceAttributes] = useState([]);

  // Row grouping state
  const [groupingModalOpened, setGroupingModalOpened] = useState(false);
  const [groupByColumn, setGroupByColumn] = useState("");
  const [collectColumns, setCollectColumns] = useState([]);

  // Linked import state for preview
  const [linkedImport, setLinkedImport] = useState(null);

  // Preview state
  const [previewOpened, setPreviewOpened] = useState(false);
  const togglePreview = () => setPreviewOpened((prev) => !prev);
  const [previewInput, setPreviewInput] = useState(
    '{\n  "title": "Sample Product",\n  "sku": "ABC-123"\n}'
  );
  const [previewResults, setPreviewResults] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  // AI Mapping suggestions state
  const [aiSuggestionsOpened, setAiSuggestionsOpened] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [imports, setImports] = useState([]);
  const [selectedImportId, setSelectedImportId] = useState(null);
  const [importsLoading, setImportsLoading] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const data = await getMappingProfileById(params.id);
        setProfile(data);
        setEditName(data.name);

        // Load schema names, source attributes, and target attributes in parallel
        const [sourceSch, targetSch, targetAttrs, sourceAttrs] = await Promise.all([
          data.sourceSchemaId ? getPimSchemaById(data.sourceSchemaId).catch(() => null) : null,
          data.targetSchemaId ? getPimSchemaById(data.targetSchemaId).catch(() => null) : null,
          data.targetSchemaId ? getPimAttributeDefinitions(data.targetSchemaId).catch(() => []) : [],
          data.sourceSchemaId ? getPimAttributeDefinitions(data.sourceSchemaId).catch(() => []) : [],
        ]);
        setSourceSchema(sourceSch);
        setTargetSchema(targetSch);
        setTargetAttributes(targetAttrs || []);
        setSourceAttributes(sourceAttrs || []);

        // If there's a linked import, load it and auto-populate preview
        if (data.linkedImportId) {
          try {
            const importData = await getImport(data.linkedImportId);
            setLinkedImport(importData);

            // Auto-run preview if we have rules
            if (data.rules && data.rules.length > 0) {
              setPreviewLoading(true);
              const previewResult = await previewMappingProfile(params.id, {
                importId: data.linkedImportId,
                sampleSize: 5,
              });
              setPreviewResults(previewResult);
              // Auto-expand preview section when we have results
              setPreviewOpened(true);
            }
          } catch {
            // Silently fail - linked import may have been deleted
          } finally {
            setPreviewLoading(false);
          }
        }
      } catch {
        notifications.show({
          color: "red",
          title: t("mappings.common.errorTitle"),
          message: t("mappings.profile.loadFailed"),
        });
        navigate("/mapping-profiles");
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [params.id, navigate]);

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t("mappings.deleteProfile.title"),
      children: (
        <Stack gap="sm">
          <Text>
            {t("mappings.deleteProfile.bodyBefore")}
            <strong>{profile?.name}</strong>
            {t("mappings.deleteProfile.bodyAfter")}
          </Text>
          <Text c="red" size="sm" fw={600}>
            {t("mappings.deleteProfile.warning")}
          </Text>
        </Stack>
      ),
      labels: {
        confirm: t("actions.delete"),
        cancel: t("common:generic.action_cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteMappingProfile(params.id);
          notifications.show({
            color: "green",
            title: t("mappings.common.successTitle"),
            message: t("mappings.deleteProfile.success"),
          });
          navigate("/mapping-profiles");
        } catch {
          notifications.show({
            color: "red",
            title: t("mappings.common.errorTitle"),
            message: t("mappings.deleteProfile.failed"),
          });
        }
      },
    });
  };

  const handleSaveName = async () => {
    if (!editName.trim()) {
      notifications.show({
        color: "red",
        title: t("mappings.common.errorTitle"),
        message: t("mappings.profile.nameEmpty"),
      });
      return;
    }

    try {
      setSaving(true);
      const updated = await updateMappingProfile(params.id, {
        name: editName.trim(),
      });
      setProfile(updated);
      setEditModalOpened(false);
      notifications.show({
        color: "green",
        title: t("mappings.common.successTitle"),
        message: t("mappings.profile.nameUpdated"),
      });
    } catch {
      notifications.show({
        color: "red",
        title: t("mappings.common.errorTitle"),
        message: t("mappings.profile.nameUpdateFailed"),
      });
    } finally {
      setSaving(false);
    }
  };

  // Helper to get attribute name from ID
  const getAttributeName = (attributeId) => {
    const attr = targetAttributes.find((a) => a.id === attributeId);
    return attr?.labels?.en || attr?.code || attributeId;
  };

  const formatPipeline = (pipeline) => {
    if (!pipeline || !pipeline.operation) {
      return t("mappings.profile.noOperation");
    }
    const formatOperation = (op) => {
      if (!op) return "?";
      const args = op.args || [];
      const formattedArgs = args.map((arg) => {
        if (typeof arg === "object" && arg.type) {
          return formatOperation(arg);
        }
        return String(arg);
      });
      return `${op.type || "Unknown"}(${formattedArgs.join(", ")})`;
    };
    return formatOperation(pipeline.operation);
  };

  const handleDeleteRule = (ruleIndex) => {
    modals.openConfirmModal({
      title: t("mappings.profile.deleteRule.title"),
      children: <Text>{t("mappings.profile.deleteRule.body")}</Text>,
      labels: {
        confirm: t("actions.delete"),
        cancel: t("common:generic.action_cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          const updatedRules = profile.rules.filter((_, i) => i !== ruleIndex);
          const updated = await updateMappingProfile(params.id, {
            rules: updatedRules,
          });
          setProfile(updated);
          notifications.show({
            color: "green",
            title: t("mappings.common.successTitle"),
            message: t("mappings.profile.ruleDeleted"),
          });
        } catch {
          notifications.show({
            color: "red",
            title: t("mappings.common.errorTitle"),
            message: t("mappings.profile.ruleDeleteFailed"),
          });
        }
      },
    });
  };

  const handleSaveRule = async (rule) => {
    const updatedRules = [...(profile.rules || []), rule];
    const updated = await updateMappingProfile(params.id, {
      rules: updatedRules,
    });
    setProfile(updated);
    notifications.show({
      color: "green",
      title: t("mappings.common.successTitle"),
      message: t("mappings.profile.ruleAdded"),
    });
  };

  const handleRunPreview = async () => {
    let sampleData;
    try {
      sampleData = JSON.parse(previewInput);
    } catch {
      notifications.show({
        color: "red",
        title: t("mappings.profile.invalidJsonTitle"),
        message: t("mappings.profile.invalidJsonBody"),
      });
      return;
    }

    // Ensure it's an array
    const dataArray = Array.isArray(sampleData) ? sampleData : [sampleData];

    try {
      setPreviewLoading(true);
      setPreviewResults(null);
      const result = await previewMappingProfile(params.id, {
        sampleData: dataArray,
      });
      setPreviewResults(result);
    } catch {
      notifications.show({
        color: "red",
        title: t("mappings.profile.previewFailedTitle"),
        message: t("mappings.profile.previewFailedBody"),
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleRowExpand = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // AI-Assisted Mapping handlers
  const handleOpenAiSuggestions = async () => {
    setAiSuggestionsOpened(true);
    setAiSuggestions([]);
    setSelectedSuggestions({});
    setSelectedImportId(null);

    // Load available imports
    try {
      setImportsLoading(true);
      const importsList = await getImports();
      setImports(importsList);
    } catch {
      notifications.show({
        color: "red",
        title: t("mappings.common.errorTitle"),
        message: t("mappings.profile.importsLoadFailed"),
      });
    } finally {
      setImportsLoading(false);
    }
  };

  const handleGetSuggestions = async () => {
    if (!selectedImportId) {
      notifications.show({
        color: "yellow",
        title: t("mappings.profile.selectImportTitle"),
        message: t("mappings.profile.selectImportBody"),
      });
      return;
    }

    try {
      setAiSuggestionsLoading(true);
      const suggestions = await suggestMappings(selectedImportId, {
        targetPimSchemaId: profile.targetSchemaId,
      });
      setAiSuggestions(suggestions);
      // Auto-select all suggestions by default
      const allSelected = {};
      suggestions.forEach((_, i) => {
        allSelected[i] = true;
      });
      setSelectedSuggestions(allSelected);
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("mappings.common.errorTitle"),
        message: error.message || t("mappings.profile.suggestionsFailed"),
      });
    } finally {
      setAiSuggestionsLoading(false);
    }
  };

  const toggleSuggestionSelection = (index) => {
    setSelectedSuggestions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleApplySuggestions = async () => {
    const selectedItems = aiSuggestions.filter((_, i) => selectedSuggestions[i]);
    if (selectedItems.length === 0) {
      notifications.show({
        color: "yellow",
        title: t("mappings.profile.noSuggestionsTitle"),
        message: t("mappings.profile.noSuggestionsBody"),
      });
      return;
    }

    try {
      setSaving(true);

      // Find target attribute IDs for the suggestions
      const newRules = selectedItems.map((suggestion) => {
        // Find the target attribute by code
        const targetAttr = targetAttributes.find(
          (a) => a.code === suggestion.targetField
        );
        return {
          destinationAttributeId: targetAttr?.id || null,
          pipeline: {
            operation: {
              type: "GetAttribute",
              args: [{ type: "Constant", args: [suggestion.sourceField] }],
            },
          },
        };
      });

      // Merge with existing rules (avoid duplicates by destinationAttributeId)
      const existingDestIds = new Set(
        profile.rules.map((r) => r.destinationAttributeId)
      );
      const rulesToAdd = newRules.filter(
        (r) => !existingDestIds.has(r.destinationAttributeId)
      );
      const mergedRules = [...profile.rules, ...rulesToAdd];

      // Update the profile
      const updated = await updateMappingProfile(profile.id, {
        rules: mergedRules,
      });

      setProfile(updated);
      setAiSuggestionsOpened(false);

      notifications.show({
        color: "green",
        title: t("mappings.profile.suggestionsAppliedTitle"),
        message: t("mappings.profile.suggestionsAppliedMsg", {
          count: rulesToAdd.length,
        }),
      });
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("mappings.common.errorTitle"),
        message: error.message || t("mappings.profile.applySuggestionsFailed"),
      });
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (result) => {
    if (result.errors && result.errors.length > 0) {
      return (
        <ThemeIcon color="red" size="sm" radius="xl">
          <IconX size={12} />
        </ThemeIcon>
      );
    }
    if (result.warnings && result.warnings.length > 0) {
      return (
        <ThemeIcon color="yellow" size="sm" radius="xl">
          <IconAlertTriangle size={12} />
        </ThemeIcon>
      );
    }
    return (
      <ThemeIcon color="green" size="sm" radius="xl">
        <IconCheck size={12} />
      </ThemeIcon>
    );
  };

  if (loading) {
    return (
      <Container size="lg">
        <Center pt="xl">
          <Loader size="xl" />
        </Center>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container size="lg" py="xl">
        <Text>{t("mappings.profile.notFound")}</Text>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              onClick={() => navigate("/mapping-profiles")}
            >
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Group gap="xs">
                <Title order={2}>{profile.name}</Title>
                <Tooltip label={t("mappings.profile.editName")}>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => setEditModalOpened(true)}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Text size="sm" c="dimmed">
                {t("mappings.profile.subtitle")}
              </Text>
            </div>
          </Group>
          <Group>
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleDelete}
            >
              {t("actions.delete")}
            </Button>
          </Group>
        </Group>

        {/* Metadata Card */}
        <Card withBorder shadow="sm" padding="lg">
          <Title order={4} mb="md">
            {t("mappings.profile.detailsTitle")}
          </Title>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack gap="sm">
                <div>
                  <Text size="sm" c="dimmed">
                    {t("mappings.profile.sourceSchema")}
                  </Text>
                  <Group gap="xs">
                    <Link href={`/pim/schemas/${profile.sourceSchemaId}`}>
                      <Text c="blue" style={{ cursor: "pointer" }}>
                        {sourceSchema?.labels?.en || sourceSchema?.name || profile.sourceSchemaId}
                      </Text>
                    </Link>
                  </Group>
                </div>
                <div>
                  <Text size="sm" c="dimmed">
                    {t("mappings.targetSchema")}
                  </Text>
                  <Group gap="xs">
                    <Link href={`/pim/schemas/${profile.targetSchemaId}`}>
                      <Text c="blue" style={{ cursor: "pointer" }}>
                        {targetSchema?.labels?.en || targetSchema?.name || profile.targetSchemaId}
                      </Text>
                    </Link>
                  </Group>
                </div>
                {linkedImport && (
                  <div>
                    <Text size="sm" c="dimmed">
                      {t("mappings.profile.sourceImport")}
                    </Text>
                    <Group gap="xs">
                      <Link href={`/imports/${profile.linkedImportId}`}>
                        <Text c="blue" style={{ cursor: "pointer" }}>
                          {linkedImport.originalName}
                        </Text>
                      </Link>
                    </Group>
                  </div>
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack gap="sm">
                <div>
                  <Text size="sm" c="dimmed">
                    {t("mappings.profile.rulesCount")}
                  </Text>
                  <Badge variant="light">
                    {t("mappings.profile.rulesBadge", {
                      count: profile.ruleCount,
                    })}
                  </Badge>
                </div>
                <div>
                  <Text size="sm" c="dimmed">
                    {t("mappings.created")}
                  </Text>
                  <Text size="sm">
                    <Timeago date={profile.createdAt} />
                  </Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed">
                    {t("mappings.profile.lastUpdated")}
                  </Text>
                  <Text size="sm">
                    <Timeago date={profile.updatedAt} />
                  </Text>
                </div>
              </Stack>
            </Grid.Col>
          </Grid>

          <Group gap="md" mt="md">
            <Badge
              variant="outline"
              color={profile.allowUnknown ? "green" : "gray"}
            >
              {profile.allowUnknown
                ? t("mappings.profile.allowUnknown")
                : t("mappings.profile.rejectUnknown")}
            </Badge>
            <Badge
              variant="outline"
              color={profile.allowUnmapped ? "green" : "gray"}
            >
              {profile.allowUnmapped
                ? t("mappings.profile.allowUnmapped")
                : t("mappings.profile.requireAllMapped")}
            </Badge>
          </Group>
        </Card>

        {/* Row Grouping Card */}
        <Card withBorder shadow="sm" padding="lg">
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconRowInsertBottom size={20} />
              <Title order={4}>{t("mappings.profile.rowGrouping.title")}</Title>
            </Group>
            {profile.rowGrouping ? (
              <Group>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconEdit size={14} />}
                  onClick={() => {
                    setGroupByColumn(profile.rowGrouping.groupByColumn);
                    setCollectColumns(profile.rowGrouping.collectColumns || []);
                    setGroupingModalOpened(true);
                  }}
                >
                  {t("mappings.profile.rowGrouping.edit")}
                </Button>
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  leftSection={<IconTrash size={14} />}
                  onClick={async () => {
                    try {
                      const updated = await updateMappingProfile(params.id, {
                        clearRowGrouping: true,
                      });
                      setProfile(updated);
                      notifications.show({
                        color: "green",
                        title: t("mappings.common.successTitle"),
                        message: t("mappings.profile.rowGrouping.removed"),
                      });
                    } catch {
                      notifications.show({
                        color: "red",
                        title: t("mappings.common.errorTitle"),
                        message: t("mappings.profile.rowGrouping.removeFailed"),
                      });
                    }
                  }}
                >
                  {t("mappings.profile.rowGrouping.remove")}
                </Button>
              </Group>
            ) : (
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => {
                  setGroupByColumn("");
                  setCollectColumns([]);
                  setGroupingModalOpened(true);
                }}
              >
                {t("mappings.profile.rowGrouping.configure")}
              </Button>
            )}
          </Group>
          {profile.rowGrouping ? (
            <Stack gap="xs">
              <div>
                <Text size="sm" c="dimmed">
                  {t("mappings.profile.rowGrouping.groupByColumn")}
                </Text>
                <Badge variant="light">{profile.rowGrouping.groupByColumn}</Badge>
              </div>
              {profile.rowGrouping.collectColumns?.length > 0 && (
                <div>
                  <Text size="sm" c="dimmed">
                    {t("mappings.profile.rowGrouping.collectFor")}
                  </Text>
                  <Group gap="xs" mt={4}>
                    {profile.rowGrouping.collectColumns.map((col) => (
                      <Badge key={col} variant="outline" size="sm">
                        {col}
                      </Badge>
                    ))}
                  </Group>
                </div>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              {t("mappings.profile.rowGrouping.emptyHint")}
            </Text>
          )}
        </Card>

        {/* Rules Section Header */}
        <Card withBorder shadow="sm" padding="lg">
          <Group justify="space-between" mb="md">
            <Title order={4}>{t("mappings.profile.rulesTitle")}</Title>
            <Group>
              <Button
                variant="light"
                leftSection={<IconSparkles size={16} />}
                size="sm"
                onClick={handleOpenAiSuggestions}
              >
                {t("mappings.profile.aiAssisted")}
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                size="sm"
                onClick={() => setRuleEditorOpened(true)}
              >
                {t("mappings.profile.addRule")}
              </Button>
            </Group>
          </Group>

          {/* Separate rules into structural and attribute mappings */}
          {(() => {
            // hasMappingRole, not `!== "Unknown"`: the wire sends camelCase, so the PascalCase
            // comparison this carried put every role-bearing rule in the ATTRIBUTE bucket and
            // rendered every badge gray with a raw camelCase label.
            const structuralRules = (profile.rules || [])
              .map((rule, index) => ({ rule, index }))
              .filter(({ rule }) => hasMappingRole(rule.role));
            const attributeRules = (profile.rules || [])
              .map((rule, index) => ({ rule, index }))
              .filter(({ rule }) => !hasMappingRole(rule.role));

            const renderRule = ({ rule, index }) => (
              <Card key={index} withBorder padding="sm" radius="sm">
                <Group justify="space-between" wrap="nowrap">
                  <Group
                    gap="md"
                    wrap="nowrap"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Badge variant="light" color="blue">
                      {getAttributeName(rule.destinationAttributeId)}
                    </Badge>
                    {hasMappingRole(rule.role) && (
                      <Badge
                        size="xs"
                        variant="dot"
                        color={mappingRoleColor(rule.role)}
                      >
                        {mappingRoleLabel(rule.role, t)}
                      </Badge>
                    )}
                    <Group gap="xs" wrap="nowrap">
                      <IconArrowsExchange size={16} color="gray" />
                      <Text
                        size="sm"
                        c="dimmed"
                        truncate
                        style={{ maxWidth: 350 }}
                      >
                        {formatPipeline(rule.pipeline)}
                      </Text>
                    </Group>
                  </Group>
                  <Tooltip label={t("mappings.profile.deleteRuleTooltip")}>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleDeleteRule(index)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Card>
            );

            if (profile.rules && profile.rules.length > 0) {
              return (
                <Stack gap="lg">
                  {/* Structural Fields Section */}
                  <div>
                    <Group gap="xs" mb="sm">
                      <Text fw={500} size="sm">{t("mappings.profile.structuralFields")}</Text>
                      <Badge size="xs" variant="light" color="violet">
                        {structuralRules.length}
                      </Badge>
                    </Group>
                    {structuralRules.length > 0 ? (
                      <Stack gap="xs">
                        {structuralRules.map(renderRule)}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed" fs="italic">
                        {t("mappings.profile.noStructuralRules")}
                      </Text>
                    )}
                  </div>

                  {/* Attributes Section */}
                  <div>
                    <Group gap="xs" mb="sm">
                      <Text fw={500} size="sm">{t("mappings.profile.attributes")}</Text>
                      <Badge size="xs" variant="light" color="gray">
                        {attributeRules.length}
                      </Badge>
                    </Group>
                    {attributeRules.length > 0 ? (
                      <Stack gap="xs">
                        {attributeRules.map(renderRule)}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed" fs="italic">
                        {t("mappings.profile.noAttributeRules")}
                      </Text>
                    )}
                  </div>
                </Stack>
              );
            }

            return (
              <Stack align="center" gap="md" py="xl">
                <Text c="dimmed">{t("mappings.profile.noRules")}</Text>
                <Text size="sm" c="dimmed">
                  {t("mappings.profile.noRulesHint")}
                </Text>
              </Stack>
            );
          })()}
        </Card>

        {/* Preview Section */}
        <Card withBorder shadow="sm" padding="lg">
          <Group
            justify="space-between"
            mb={previewOpened ? "md" : 0}
            style={{ cursor: "pointer" }}
            onClick={togglePreview}
          >
            <Group gap="xs">
              {previewOpened ? (
                <IconChevronDown size={20} />
              ) : (
                <IconChevronRight size={20} />
              )}
              <Title order={4}>{t("mappings.profile.preview.title")}</Title>
            </Group>
          </Group>
          <Collapse expanded={previewOpened}>
            <Stack gap="md">
              <Textarea
                label={t("mappings.profile.preview.sampleLabel")}
                description={t("mappings.profile.preview.sampleDescription")}
                placeholder='{"title": "Sample Product", "sku": "ABC-123"}'
                value={previewInput}
                onChange={(e) => setPreviewInput(e.currentTarget.value)}
                minRows={4}
                autosize
                maxRows={10}
              />
              <Group>
                <Button
                  leftSection={<IconPlayerPlay size={16} />}
                  onClick={handleRunPreview}
                  loading={previewLoading}
                >
                  {t("mappings.profile.preview.run")}
                </Button>
              </Group>

              {previewResults && previewResults.results && (
                <div>
                  <Text fw={500} mb="xs">
                    {t("mappings.profile.preview.results", {
                      count: previewResults.results.length,
                    })}
                  </Text>
                  <Table striped withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 40 }}></Table.Th>
                        <Table.Th>{t("mappings.profile.preview.targetAttribute")}</Table.Th>
                        <Table.Th>{t("mappings.profile.preview.transformedValue")}</Table.Th>
                        <Table.Th style={{ width: 60 }}>{t("mappings.status")}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {previewResults.results.map((result, resultIdx) => (
                        <React.Fragment key={`result-${resultIdx}`}>
                          {result.mappedData &&
                            Object.entries(result.mappedData).map(
                              ([key, value], attrIdx) => (
                                <Table.Tr
                                  key={`rowAttr-${resultIdx}-${attrIdx}`}
                                  style={
                                    attrIdx === 0
                                      ? {
                                          borderTop:
                                            "2px solid var(--mantine-color-default-border)",
                                        }
                                      : {}
                                  }
                                >
                                  {attrIdx === 0 && (
                                    <Table.Td
                                      rowSpan={
                                        Object.keys(result.mappedData).length ||
                                        1
                                      }
                                      style={{ verticalAlign: "top" }}
                                    >
                                      {((result.errors && result.errors.length > 0) ||
                                        (result.warnings && result.warnings.length > 0)) ? (
                                        <ActionIcon
                                          variant="subtle"
                                          size="sm"
                                          onClick={() =>
                                            toggleRowExpand(resultIdx)
                                          }
                                        >
                                          {expandedRows[resultIdx] ? (
                                            <IconChevronDown size={14} />
                                          ) : (
                                            <IconChevronRight size={14} />
                                          )}
                                        </ActionIcon>
                                      ) : null}
                                    </Table.Td>
                                  )}
                                  <Table.Td>
                                    <Badge variant="light" size="sm">
                                      {key}
                                    </Badge>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text
                                      size="sm"
                                      truncate
                                      style={{ maxWidth: 300 }}
                                    >
                                      {String(value ?? "")}
                                    </Text>
                                  </Table.Td>
                                  {attrIdx === 0 && (
                                    <Table.Td
                                      rowSpan={
                                        Object.keys(result.mappedData).length ||
                                        1
                                      }
                                      style={{ verticalAlign: "top" }}
                                    >
                                      {getStatusIcon(result)}
                                    </Table.Td>
                                  )}
                                </Table.Tr>
                              )
                            )}
                          {expandedRows[resultIdx] &&
                            ((result.errors && result.errors.length > 0) ||
                              (result.warnings &&
                                result.warnings.length > 0)) && (
                              <Table.Tr style={{ backgroundColor: "var(--mantine-color-default-hover)" }}>
                                <Table.Td colSpan={4}>
                                  <Stack gap="xs" py="sm" px="md">
                                    {result.errors?.map((err, i) => (
                                      <Group key={`err-${i}`} gap="xs">
                                        <Badge size="xs" color="red">{t("mappings.profile.preview.error")}</Badge>
                                        <Text size="sm" c="red">
                                          {err?.attributeCode}: {err?.message}
                                        </Text>
                                      </Group>
                                    ))}
                                    {result.warnings?.map((warn, i) => (
                                      <Group key={`warn-${i}`} gap="xs">
                                        <Badge size="xs" color="yellow">{t("mappings.profile.preview.warning")}</Badge>
                                        <Text size="sm" c="yellow">
                                          {warn?.attributeCode}: {warn?.message}
                                        </Text>
                                      </Group>
                                    ))}
                                  </Stack>
                                </Table.Td>
                              </Table.Tr>
                            )}
                        </React.Fragment>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              )}
            </Stack>
          </Collapse>
        </Card>
      </Stack>

      {/* Edit Name Modal */}
      <Modal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        title={t("mappings.profile.editNameTitle")}
      >
        <Stack gap="md">
          <TextInput
            label={t("mappings.profile.profileName")}
            value={editName}
            onChange={(e) => setEditName(e.currentTarget.value)}
            placeholder={t("mappings.profile.profileNamePlaceholder")}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEditModalOpened(false)}>
              {t("common:generic.action_cancel")}
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSaveName}
              loading={saving}
            >
              {t("common:generic.action_save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Rule Editor Modal */}
      <RuleEditor
        opened={ruleEditorOpened}
        onClose={() => setRuleEditorOpened(false)}
        onSave={handleSaveRule}
        sourceSchemaId={profile.sourceSchemaId}
        targetSchemaId={profile.targetSchemaId}
      />

      {/* Row Grouping Modal */}
      <Modal
        opened={groupingModalOpened}
        onClose={() => setGroupingModalOpened(false)}
        title={t("mappings.profile.rowGrouping.modalTitle")}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t("mappings.profile.rowGrouping.modalHint")}
          </Text>
          <Select
            label={t("mappings.profile.rowGrouping.groupByColumn")}
            description={t("mappings.profile.rowGrouping.groupByDescription")}
            placeholder={t("mappings.profile.rowGrouping.selectColumn")}
            data={sourceAttributes.map((a) => ({
              value: a.code,
              label: a.labels?.en || a.code,
            }))}
            value={groupByColumn}
            onChange={(val) => {
              setGroupByColumn(val || "");
              // Remove from collect columns if selected as group-by
              if (val) setCollectColumns((prev) => prev.filter((c) => c !== val));
            }}
            searchable
            clearable
          />
          <MultiSelect
            label={t("mappings.profile.rowGrouping.collectFor")}
            description={t("mappings.profile.rowGrouping.collectDescription")}
            placeholder={t("mappings.profile.rowGrouping.selectColumns")}
            data={sourceAttributes
              .filter((a) => a.code !== groupByColumn)
              .map((a) => ({
                value: a.code,
                label: a.labels?.en || a.code,
              }))}
            value={collectColumns}
            onChange={setCollectColumns}
            searchable
          />
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => setGroupingModalOpened(false)}
            >
              {t("common:generic.action_cancel")}
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              disabled={!groupByColumn}
              onClick={async () => {
                try {
                  setSaving(true);
                  const updated = await updateMappingProfile(params.id, {
                    rowGrouping: {
                      groupByColumn,
                      collectColumns,
                    },
                  });
                  setProfile(updated);
                  setGroupingModalOpened(false);
                  notifications.show({
                    color: "green",
                    title: t("mappings.common.successTitle"),
                    message: t("mappings.profile.rowGrouping.saved"),
                  });
                } catch {
                  notifications.show({
                    color: "red",
                    title: t("mappings.common.errorTitle"),
                    message: t("mappings.profile.rowGrouping.saveFailed"),
                  });
                } finally {
                  setSaving(false);
                }
              }}
              loading={saving}
            >
              {t("common:generic.action_save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* AI Suggestions Modal */}
      <Modal
        opened={aiSuggestionsOpened}
        onClose={() => setAiSuggestionsOpened(false)}
        title={
          <Group gap="xs">
            <IconSparkles size={20} />
            <Text fw={500}>{t("mappings.profile.ai.title")}</Text>
          </Group>
        }
        size="xl"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t("mappings.profile.ai.intro")}
          </Text>

          {aiSuggestions.length === 0 && !aiSuggestionsLoading && (
            <Stack gap="md">
              <Select
                label={t("mappings.profile.ai.selectImport")}
                placeholder={
                  importsLoading
                    ? t("mappings.profile.ai.loadingImports")
                    : t("mappings.profile.ai.chooseImport")
                }
                data={imports.map((imp) => ({
                  value: imp.id,
                  label: t("mappings.profile.ai.importOption", {
                    name: imp.originalName,
                    count: imp.columnCount || 0,
                  }),
                }))}
                value={selectedImportId}
                onChange={setSelectedImportId}
                disabled={importsLoading}
                searchable
                clearable
              />
              {imports.length === 0 && !importsLoading && (
                <Text size="sm" c="dimmed">
                  {t("mappings.profile.ai.noImports")}
                </Text>
              )}
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  onClick={() => setAiSuggestionsOpened(false)}
                >
                  {t("common:generic.action_cancel")}
                </Button>
                <Button
                  leftSection={<IconSparkles size={16} />}
                  onClick={handleGetSuggestions}
                  disabled={!selectedImportId}
                >
                  {t("mappings.profile.ai.getSuggestions")}
                </Button>
              </Group>
            </Stack>
          )}

          {aiSuggestionsLoading && (
            <Center py="xl">
              <Loader size="md" />
            </Center>
          )}

          {aiSuggestions.length > 0 && (
            <>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 40 }}></Table.Th>
                    <Table.Th>{t("mappings.profile.ai.sourceField")}</Table.Th>
                    <Table.Th>{t("mappings.profile.ai.targetField")}</Table.Th>
                    <Table.Th>{t("mappings.profile.ai.confidence")}</Table.Th>
                    <Table.Th>{t("mappings.profile.ai.reason")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {aiSuggestions.map((suggestion, index) => (
                    <Table.Tr
                      key={index}
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSuggestionSelection(index)}
                    >
                      <Table.Td>
                        <input
                          type="checkbox"
                          checked={selectedSuggestions[index] || false}
                          onChange={() => toggleSuggestionSelection(index)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">{suggestion.sourceField}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Text size="sm" ff="monospace">{suggestion.targetField}</Text>
                          {suggestion.targetRole && (
                            <Badge size="xs" variant="dot" color="violet">
                              {suggestion.targetRole}
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={
                            suggestion.confidence >= 0.9
                              ? "green"
                              : suggestion.confidence >= 0.7
                                ? "yellow"
                                : "gray"
                          }
                        >
                          {Math.round(suggestion.confidence * 100)}%
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {suggestion.reason === "name_match"
                            ? t("mappings.profile.ai.reasonNameMatch")
                            : suggestion.reason === "role_inference"
                              ? t("mappings.profile.ai.reasonRolePattern")
                              : suggestion.reason}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  onClick={() => setAiSuggestionsOpened(false)}
                >
                  {t("common:generic.action_cancel")}
                </Button>
                <Button
                  leftSection={<IconCheck size={16} />}
                  onClick={handleApplySuggestions}
                >
                  {t("mappings.profile.ai.applySelected")}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Container>
  );
}
